import { useEffect, useState } from "react";
import axios from "axios";
import { DownloadSimple, FileText, Truck } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GSTR1() {
  const [mode, setMode] = useState("month"); // "month" | "fy"
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  // FY start year — e.g. 2025 means Apr-2025 to Mar-2026. Default = current FY.
  const [fyStart, setFyStart] = useState(() => {
    const d = new Date();
    return d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Compact "?month=YYYY-MM" or "?fy=YYYY" query builder — used by all
  // three endpoints (report / csv / excel) so they stay in lockstep.
  const buildQuery = () => (mode === "fy" ? `fy=${fyStart}` : `month=${month}`);
  const fileSlug = () => (mode === "fy" ? `FY_${fyStart}-${String((fyStart + 1) % 100).padStart(2, "0")}` : month);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/gstr1?${buildQuery()}`);
      setData(response.data);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); }, [mode, month, fyStart]);

  const handleDownloadCSV = async () => {
    try {
      const response = await axios.get(`${API}/reports/gstr1/csv?${buildQuery()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `GSTR1_${fileSlug()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error downloading CSV:", error);
    }
  };

  const handleDownloadExcel = async () => {
    try {
      const response = await axios.get(`${API}/reports/gstr1/excel?${buildQuery()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `GSTR1_${fileSlug()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error downloading Excel:", error);
      alert("Excel download failed: " + (error.response?.data?.detail || error.message));
    }
  };

  const handleDownloadTallyXml = async () => {
    try {
      const response = await axios.get(`${API}/reports/gstr1/tally-xml?${buildQuery()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Sales_Tally_${fileSlug()}.xml`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert(error.response?.status === 404 ? "No sales invoices found for the selected period" : "Tally XML download failed");
    }
  };

  const fmt = (v) => `\u20b9${(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="p-4 sm:p-8" data-testid="gstr1-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-slate-900 mb-2">GSTR-1 Export</h1>
          <p className="text-slate-500 text-sm">Generate GSTR-1 format data for tax filing</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={handleDownloadExcel} data-testid="download-gstr1-excel" className="px-4 py-2 bg-emerald-600 text-white rounded-sm text-sm font-semibold hover:bg-emerald-700 transition-all duration-200 flex items-center gap-2">
            <DownloadSimple size={20} weight="bold" /> Download Excel
          </button>
          <button onClick={handleDownloadTallyXml} data-testid="download-gstr1-tally-xml" title="Download Tally-import XML — sales vouchers with customer GSTIN, HSN, qty, rate, taxes (masters included)" className="px-4 py-2 bg-purple-700 text-white rounded-sm text-sm font-semibold hover:bg-purple-800 transition-all duration-200 flex items-center gap-2">
            <DownloadSimple size={20} weight="bold" /> Tally XML
          </button>
          <button onClick={handleDownloadCSV} data-testid="download-gstr1-csv" className="px-4 py-2 bg-slate-900 text-white rounded-sm text-sm font-semibold hover:bg-slate-800 transition-all duration-200 flex items-center gap-2">
            <DownloadSimple size={20} weight="bold" /> Download CSV
          </button>
        </div>
      </div>

      <div className="mb-6 flex items-end gap-3 flex-wrap" data-testid="gstr1-period-picker">
        <div className="flex gap-1 bg-slate-100 p-1 rounded-sm">
          <button
            data-testid="gstr1-mode-month"
            onClick={() => setMode("month")}
            className={`px-4 py-2 text-xs font-semibold rounded-sm transition-all ${mode === "month" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"}`}
          >Month</button>
          <button
            data-testid="gstr1-mode-fy"
            onClick={() => setMode("fy")}
            className={`px-4 py-2 text-xs font-semibold rounded-sm transition-all ${mode === "fy" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"}`}
          >Financial Year (Apr–Mar)</button>
        </div>
        {mode === "month" ? (
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-2">Select Month</label>
            <input type="month" data-testid="gstr1-month-picker" value={month} onChange={(e) => setMonth(e.target.value)} className="px-4 py-2 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-semibold" />
          </div>
        ) : (
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-2">Select Financial Year</label>
            <select
              data-testid="gstr1-fy-picker"
              value={fyStart}
              onChange={(e) => setFyStart(Number(e.target.value))}
              className="px-4 py-2 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-semibold"
            >
              {Array.from({ length: 8 }, (_, i) => {
                const y = new Date().getFullYear() + 1 - i;
                return <option key={y} value={y}>{`FY ${y}-${String((y + 1) % 100).padStart(2, "0")} (Apr ${y} – Mar ${y + 1})`}</option>;
              })}
            </select>
          </div>
        )}
      </div>

      {loading ? (
        <div className="animate-pulse space-y-4">{[1, 2, 3].map((i) => <div key={i} className="h-20 bg-slate-200 rounded"></div>)}</div>
      ) : data ? (
        <div className="space-y-8">
          {/* Business Info */}
          {data.business_name && (
            <div className="bg-white border border-slate-200 rounded-sm p-5 flex items-center gap-4">
              <FileText size={32} weight="duotone" className="text-slate-600" />
              <div>
                <p className="text-sm font-bold text-slate-900">{data.business_name}</p>
                <p className="text-xs text-slate-500 font-mono">GSTIN: {data.gstin}</p>
                <p className="text-xs text-slate-500">Period: {data.period || (mode === "fy" ? `FY ${fyStart}-${String((fyStart + 1) % 100).padStart(2, "0")}` : month)}</p>
              </div>
            </div>
          )}

          {/* B2B Section */}
          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900">B2B Invoices</h2>
              <p className="text-sm text-slate-500">Business-to-Business (Registered Dealers) - {data.b2b.totals.count} invoices</p>
            </div>
            {data.b2b.invoices.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No B2B invoices for this period</div>
            ) : (
              <div className="overflow-x-auto sticky-table">
                <table className="w-full">
                  <thead className="sticky-thead bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-600">GSTIN</th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-600">Customer</th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-600">Invoice</th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-600">Place</th>
                      <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600">Taxable</th>
                      <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600">CGST</th>
                      <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600">SGST</th>
                      <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600">IGST</th>
                      <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {data.b2b.invoices.map((inv, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-xs font-mono text-slate-600">{inv.gstin}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-slate-900">{inv.customer_name}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{inv.invoice_no}<br /><span className="text-xs">{inv.invoice_date}</span></td>
                        <td className="px-4 py-3 text-xs text-slate-500">{inv.place_of_supply}</td>
                        <td className="px-4 py-3 text-sm text-right">{fmt(inv.taxable_value)}</td>
                        <td className="px-4 py-3 text-sm text-right">{fmt(inv.cgst)}</td>
                        <td className="px-4 py-3 text-sm text-right">{fmt(inv.sgst)}</td>
                        <td className="px-4 py-3 text-sm text-right">{fmt(inv.igst)}</td>
                        <td className="px-4 py-3 text-sm font-bold text-right">{fmt(inv.invoice_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-50 border-t-2 border-slate-900 font-bold">
                    <tr>
                      <td colSpan={4} className="px-4 py-3 text-sm">Total ({data.b2b.totals.count})</td>
                      <td className="px-4 py-3 text-sm text-right">{fmt(data.b2b.totals.taxable_value)}</td>
                      <td className="px-4 py-3 text-sm text-right">{fmt(data.b2b.totals.cgst)}</td>
                      <td className="px-4 py-3 text-sm text-right">{fmt(data.b2b.totals.sgst)}</td>
                      <td className="px-4 py-3 text-sm text-right">{fmt(data.b2b.totals.igst)}</td>
                      <td className="px-4 py-3 text-sm text-right">{fmt(data.b2b.totals.invoice_value)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* B2CS Section */}
          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900">B2CS (Consumer Sales)</h2>
              <p className="text-sm text-slate-500">Business-to-Consumer (Unregistered) - {data.b2cs.totals.count} entries</p>
            </div>
            {data.b2cs.invoices.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No B2CS invoices for this period</div>
            ) : (
              <div className="overflow-x-auto sticky-table">
                <table className="w-full">
                  <thead className="sticky-thead bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-600">Place of Supply</th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-600">GST Type</th>
                      <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600">Taxable Value</th>
                      <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600">CGST</th>
                      <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600">SGST</th>
                      <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600">IGST</th>
                      <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {data.b2cs.invoices.map((inv, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm text-slate-600">{inv.place_of_supply}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{inv.gst_type}</td>
                        <td className="px-4 py-3 text-sm text-right">{fmt(inv.taxable_value)}</td>
                        <td className="px-4 py-3 text-sm text-right">{fmt(inv.cgst)}</td>
                        <td className="px-4 py-3 text-sm text-right">{fmt(inv.sgst)}</td>
                        <td className="px-4 py-3 text-sm text-right">{fmt(inv.igst)}</td>
                        <td className="px-4 py-3 text-sm font-bold text-right">{fmt(inv.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* HSN Summary */}
          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900">HSN Summary</h2>
            </div>
            {data.hsn_summary.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No HSN data</div>
            ) : (
              <div className="overflow-x-auto sticky-table">
                <table className="w-full">
                  <thead className="sticky-thead bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-600">HSN/SAC</th>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-600">Description</th>
                      <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600">Qty</th>
                      <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600">Taxable</th>
                      <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600">CGST</th>
                      <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600">SGST</th>
                      <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600">IGST</th>
                      <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {data.hsn_summary.map((h, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm font-mono text-slate-600">{h.hsn_sac}</td>
                        <td className="px-4 py-3 text-sm text-slate-900">{h.description}</td>
                        <td className="px-4 py-3 text-sm text-right">{h.quantity}</td>
                        <td className="px-4 py-3 text-sm text-right">{fmt(h.taxable_value)}</td>
                        <td className="px-4 py-3 text-sm text-right">{fmt(h.cgst)}</td>
                        <td className="px-4 py-3 text-sm text-right">{fmt(h.sgst)}</td>
                        <td className="px-4 py-3 text-sm text-right">{fmt(h.igst)}</td>
                        <td className="px-4 py-3 text-sm font-bold text-right">{fmt(h.total_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : null}

      <TransportGSTR1 fmt={fmt} />
    </div>
  );
}

function TransportGSTR1({ fmt }) {
  const { user } = useAuth();
  const enabled = !!user?.transport_invoice_enabled;
  const [mode, setMode] = useState("month"); // month | year
  const [tiMonth, setTiMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [tiYear, setTiYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const qs = () => (mode === "month" ? `month=${tiMonth}` : `year=${tiYear}`);

  useEffect(() => {
    if (!enabled) return;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/reports/gstr1-transport?${qs()}`);
        setData(r.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, mode, tiMonth, tiYear]);

  const download = async (kind) => {
    try {
      const r = await axios.get(`${API}/reports/gstr1-transport/${kind}?${qs()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `GSTR1_Transport_${mode === "month" ? tiMonth : tiYear}.${kind === "excel" ? "xlsx" : "csv"}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert("Download failed: " + (e.response?.data?.detail || e.message)); }
  };

  if (!enabled) return null;

  const th = "px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-600";
  const thr = "px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-600";
  const td = "px-4 py-3 text-sm";

  const Section = ({ title, subtitle, headers, rows, empty }) => (
    <div className="bg-white border border-slate-200 rounded-sm">
      <div className="p-6 border-b border-slate-200">
        <h2 className="text-xl font-bold text-slate-900">{title}</h2>
        {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
      </div>
      {rows.length === 0 ? (
        <div className="p-8 text-center text-slate-500">{empty}</div>
      ) : (
        <div className="overflow-x-auto sticky-table">
          <table className="w-full">
            <thead className="sticky-thead bg-slate-50 border-b border-slate-200">
              <tr>{headers.map((h, i) => <th key={i} className={i === 0 ? th : thr}>{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {rows.map((r, i) => (
                <tr key={i} className="hover:bg-slate-50">
                  {r.map((c, j) => <td key={j} className={`${td} ${j === 0 ? "text-slate-900 font-semibold" : "text-right"}`}>{c}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <div className="mt-12 space-y-6" data-testid="gstr1-transport-section">
      <div className="flex items-end justify-between flex-wrap gap-3 border-t-4 border-slate-900 pt-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tighter text-slate-900 mb-1 flex items-center gap-2">
            <Truck size={28} weight="duotone" /> GSTR 1 Transport Invoices
          </h1>
          <p className="text-slate-500 text-sm">B2B, B2CS, Customer Sales &amp; HSN summary from Transport Invoices</p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div className="flex gap-1 bg-slate-100 p-1 rounded-sm">
            {["month", "year"].map((m) => (
              <button key={m} onClick={() => setMode(m)} data-testid={`ti-gstr1-mode-${m}`}
                className={`px-3 py-1.5 text-sm font-bold rounded-sm ${mode === m ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>
                {m === "month" ? "Month-wise" : "Year-wise"}
              </button>
            ))}
          </div>
          {mode === "month" ? (
            <input type="month" data-testid="ti-gstr1-month" value={tiMonth} onChange={(e) => setTiMonth(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-sm text-sm font-semibold" />
          ) : (
            <input type="number" data-testid="ti-gstr1-year" value={tiYear} onChange={(e) => setTiYear(parseInt(e.target.value) || new Date().getFullYear())} className="px-3 py-2 border border-slate-200 rounded-sm text-sm font-semibold w-28" />
          )}
          <button onClick={() => download("excel")} data-testid="ti-gstr1-excel" className="px-4 py-2 bg-emerald-600 text-white rounded-sm text-sm font-semibold hover:bg-emerald-700 flex items-center gap-2">
            <DownloadSimple size={18} weight="bold" /> Download Excel
          </button>
          <button onClick={() => download("csv")} data-testid="ti-gstr1-csv" className="px-4 py-2 bg-slate-900 text-white rounded-sm text-sm font-semibold hover:bg-slate-800 flex items-center gap-2">
            <DownloadSimple size={18} weight="bold" /> Download CSV
          </button>
        </div>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-4">{[1, 2].map((i) => <div key={i} className="h-20 bg-slate-200 rounded"></div>)}</div>
      ) : data ? (
        <>
          <Section
            title="B2B Invoices"
            subtitle={`Registered parties (with GSTIN) — ${data.b2b.totals.count} invoices · Taxable ${fmt(data.b2b.totals.taxable_value)}`}
            headers={["Party", "GSTIN", "Invoice", "Date", "Place", "Taxable", "CGST", "SGST", "IGST", "Total"]}
            rows={data.b2b.rows.map((r) => [r.party_name, r.gstin, r.invoice_no, r.invoice_date, r.place_of_supply, fmt(r.taxable_value), fmt(r.cgst), fmt(r.sgst), fmt(r.igst), fmt(r.total)])}
            empty="No B2B transport invoices for this period"
          />
          <Section
            title="B2CS Invoices"
            subtitle={`Unregistered parties — ${data.b2cs.totals.count} invoices · Taxable ${fmt(data.b2cs.totals.taxable_value)}`}
            headers={["Party", "Invoice", "Date", "Place", "Taxable", "CGST", "SGST", "IGST", "Total"]}
            rows={data.b2cs.rows.map((r) => [r.party_name, r.invoice_no, r.invoice_date, r.place_of_supply, fmt(r.taxable_value), fmt(r.cgst), fmt(r.sgst), fmt(r.igst), fmt(r.total)])}
            empty="No B2CS transport invoices for this period"
          />
          <Section
            title="Customer Sales"
            subtitle={`Party-wise summary — ${data.customer_sales.totals.count} parties`}
            headers={["Party", "GSTIN", "Invoices", "Taxable", "CGST", "SGST", "IGST", "Total"]}
            rows={data.customer_sales.rows.map((r) => [r.party_name, r.gstin, r.invoices, fmt(r.taxable_value), fmt(r.cgst), fmt(r.sgst), fmt(r.igst), fmt(r.total)])}
            empty="No transport sales for this period"
          />
          <Section
            title="HSN Summary"
            headers={["HSN/SAC", "CN Count", "Taxable", "CGST", "SGST", "IGST", "Total Tax", "Total Value"]}
            rows={data.hsn_summary.map((h) => [h.hsn_sac, h.cn_count, fmt(h.taxable_value), fmt(h.cgst), fmt(h.sgst), fmt(h.igst), fmt(h.total_tax), fmt(h.total_value)])}
            empty="No HSN data for this period"
          />
        </>
      ) : null}
    </div>
  );
}
